import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link, Navigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import Movies from './Movies';
import TvShows from './TvShows';
import Users from './Users';
import Content from './Content/Index';
import Reports from './Reports';
import Settings from './Settings';
import Profile from './Profile';
import PaymentMethod from './PaymentMethod';
import Loader from './Loader';
import Dashboard from './Dashboard';
import ScrollProgress from './ScrollProgress';
import MediaGallery from './MediaGallery';
import TopContents from './TopContents';
import Actors from './Actors';
import Genres from './Genres';
import GenreSeries from './GenreSeries';
import Languages from './Languages';
import LiveTvCategories from './LiveTvCategories';
import LiveTvChannels from './LiveTvChannels';
import Notification from './Notification';
import Tags from './Tags';
import Types from './Types';
import Plans from './Plans';
import Episodes from './Content/Episodes';
import BannerManagement from './Content/BannerManagement';
import CurationTabs from './Content/CurationTabs';
import BulkSeriesImport from './Content/BulkSeriesImport';
import Breadcrumbs from './Breadcrumbs';
import ViewUser from './ViewUser';
import AboutUs from './Pages/AboutUs';
import PrivacyPolicy from './Pages/PrivacyPolicy';
import TermsCondition from './Pages/TermsCondition';
import AdsSetting from './AdsSetting';
import AdsSettingUpdate from './AdsSettingUpdate';
import VipPlans from './VipPlans';
import OrderHistory from './OrderHistory';
import Reward from './Settings/Reward';
import Tickets from './Tickets';
import { useAuth } from '../context/Context';
import { useTheme } from '../context/ThemeContext';
import { usePermission } from '../hooks/usePermission';
import { PERM } from '../config/permissions';
import SendNotification from './SendNotification';
import AnalyticsDashboard from './AnalyticsDashboard';
import RoleManagement from './RoleManagement';
import AdminUsers from './AdminUsers';
import UserSegments from './UserSegments';
import ContentModeration from './ContentModeration';
import ActivityFeed from './ActivityFeed';
import RealTimeAnalytics from './RealTimeAnalytics';
import SystemHealth from './SystemHealth';
import VideoPipeline from './VideoPipeline';
import PlayerAnalytics from './PlayerAnalytics';
import RevenueMonetization from './RevenueMonetization';
import SecurityCompliance from './SecurityCompliance';
import InfrastructureCost from './InfrastructureCost';
import CodebaseDevOps from './CodebaseDevOps';

/**
 * Nav mirrors the 21 PRD sections. Every entry carries the backend module key it
 * is gated on (see src/config/permissions.js) — a section the role has no `view`
 * on is removed from the nav entirely, not disabled or greyed out.
 *
 * Reward has no dedicated backend module yet; it shares the Settings endpoint,
 * so it is gated on `settings`.
 */
const navMenus = [
    { label: 'Dashboard',  icon: 'fas fa-house',       path: '/',          permission: PERM.DASHBOARD },
    { label: 'Analytics',  icon: 'fas fa-chart-line',  path: '/analytics', permission: PERM.ANALYTICS },
    { label: 'Users',      icon: 'fas fa-users',       path: '/users',     permission: PERM.USERS },
    {
        label: 'Content', icon: 'fas fa-video', children: [
            { label: 'Series',            icon: 'fas fa-video',     path: '/series',            permission: PERM.SERIES },
            { label: 'Categories',        icon: 'fas fa-box-open',  path: '/genres',            permission: PERM.CATEGORIES },
            { label: 'Genres',            icon: 'fas fa-tag',       path: '/tags',              permission: PERM.GENRES },
            { label: 'Tags',              icon: 'fas fa-box-open',  path: '/types',             permission: PERM.TAGS },
            { label: 'Banner Management', icon: 'fas fa-image',     path: '/banner-management', permission: PERM.BANNER_MANAGEMENT },
            { label: 'Curated Collection',icon: 'fas fa-fire',      path: '/curated',           permission: [PERM.SERIES, PERM.TRAILER_RANKING] },        ]
    },
    {
        label: 'Packages', icon: 'fas fa-coins', children: [
            { label: 'Coin Plans',    icon: 'fas fa-coins',           path: '/plans',         permission: PERM.PACKAGES },
            { label: 'VIP Plans',     icon: 'fas fa-project-diagram', path: '/vip-plans',     permission: PERM.PACKAGES },
            { label: 'Order History', icon: 'fas fa-trophy',          path: '/order-history', permission: 'order_history' },
        ]
    },
    {
        label: 'General', icon: 'fas fa-cog', children: [
            { label: 'Payment Method',   icon: 'fas fa-money-bill',  path: '/payment-method',   permission: PERM.PAYMENT_METHOD },
            { label: 'Languages',        icon: 'fas fa-language',    path: '/languages',        permission: PERM.LANGUAGES },
            { label: 'Reward',           icon: 'fas fa-atom',        path: '/reward',           permission: PERM.REWARD },
            { label: 'Settings',         icon: 'fas fa-cog',         path: '/settings',         permission: PERM.SETTINGS },
            { label: 'Ads Setting',      icon: 'fas fa-ad',          path: '/ads-setting',      permission: PERM.ADS_SETTING },
            { label: 'Send Notification',icon: 'fas fa-bell',        path: '/send-notification',permission: PERM.SEND_NOTIFICATION },
            { label: 'Tickets',          icon: 'fas fa-ticket-alt',  path: '/tickets',          permission: PERM.TICKETS },
        ]
    },
    {
        label: 'Admin', icon: 'fas fa-user-shield', children: [
            { label: 'Role Management',    icon: 'fas fa-user-shield', path: '/role-management',  permission: PERM.ROLE_MANAGEMENT },
            { label: 'Team Members',       icon: 'fas fa-users-cog',   path: '/admin-users',      permission: PERM.ADMIN_USERS },
            { label: 'About Us',           icon: 'fas fa-info',        path: '/about-us',         permission: PERM.ABOUT_US },
            { label: 'Privacy Policy',     icon: 'fas fa-info',        path: '/privacy-policy',   permission: PERM.PRIVACY_POLICY },
            { label: 'Terms & Conditions', icon: 'fas fa-info',        path: '/terms-conditions', permission: PERM.TERMS_AND_CONDITIONS },
            { label: 'User Segments',      icon: 'fas fa-user-tag',    path: '/user-segments',    permission: PERM.USER_SEGMENTS },
        ]
    },
];

const AccessDenied = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16, color: '#8a8a9a' }}>
        <i className="fas fa-lock" style={{ fontSize: 56, color: '#ef4444', opacity: 0.5 }}></i>
        <h4 style={{ color: '#c8c8d8', margin: 0 }}>Access Denied</h4>
        <p style={{ margin: 0, fontSize: 14 }}>You don't have permission to view this page.</p>
    </div>
);

const NoSectionsAssigned = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16, color: '#8a8a9a' }}>
        <i className="fas fa-user-lock" style={{ fontSize: 56, color: '#f59e0b', opacity: 0.5 }}></i>
        <h4 style={{ color: '#c8c8d8', margin: 0 }}>No sections assigned</h4>
        <p style={{ margin: 0, fontSize: 14 }}>Your account has no sections enabled yet. Please contact your administrator.</p>
    </div>
);

const Main = () => {
    const { logout, user } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { canView } = usePermission();
    const location = useLocation();
    const currentRoute = location.pathname.replace(/\/+$/, '');
    const userIdMatch = currentRoute.match(/^\/users\/(\d+)$/);
    const seriesIdMatch = currentRoute.match(/^\/series\/(\d+)$/);
    const adsIdMatch = currentRoute.match(/^\/ads-setting\/(\d+)$/);
    const genreSeriesMatch = currentRoute.match(/^\/genres\/(\d+)\/series$/);
    const { loading } = useSelector(state => state.VerifyTokenReducer);
    const { payload } = useSelector(state => state.GetSiteDataReducer);
    const [siteData, setSiteData] = useState({});
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const navRef = useRef(null);

    const visibleNavMenus = navMenus.map(menu => {
        if (menu.children) {
            const visibleChildren = menu.children.filter(c => !c.permission || [].concat(c.permission).some(canView));
            if (visibleChildren.length === 0) return null;
            return { ...menu, children: visibleChildren };
        }
        if (menu.permission && !canView(menu.permission)) return null;
        return menu;
    }).filter(Boolean);

    // An array permission means "any of".
    const guard = (permission, component) => [].concat(permission).some(canView) ? component : <AccessDenied />;

    useEffect(() => {
        setSiteData(payload?.responseDetails)
    }, [payload]);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.classList.add('no_scroll');
        } else {
            document.body.classList.remove('no_scroll');
        }
    }, [mobileMenuOpen]);

    useEffect(() => {
        const handleClick = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, []);

    useEffect(() => {
        setOpenDropdown(null);
        setMobileMenuOpen(false);
    }, [location.pathname]);

    // `/` is the Dashboard, so an admin whose role has no `dashboard` view was
    // landing on Access Denied even when their menu had plenty of other sections.
    // Send them to the first entry in their own nav instead.
    //
    // This sits at the render layer rather than at each redirect site, so it
    // covers every way in — sign-in, forced password change, maintenance bounce,
    // bookmark, typed URL — with one rule.
    //
    // Guarded on `user?.id` because permissions aren't known until the profile
    // is in hand; redirecting on an empty user would bounce even a Super Admin.
    const firstVisible = visibleNavMenus[0];
    const landingPath = firstVisible?.path || firstVisible?.children?.[0]?.path;
    const needsLandingRedirect = !loading && user?.id && currentRoute === '' && !canView(PERM.DASHBOARD);

    if (needsLandingRedirect && landingPath) {
        return <Navigate to={landingPath} replace />;
    }

    const isRouteMatch = (path) => {
        if (path === '/') return currentRoute === '';
        return currentRoute === path;
    };

    const isMenuActive = (menu) => {
        if (menu.path) {
            if (isRouteMatch(menu.path)) return true;
            if (menu.path === '/genres' && genreSeriesMatch) return true;
            if (menu.path === '/series' && seriesIdMatch) return true;
            return false;
        }
        if (menu.children) return menu.children.some(c => {
            if (isRouteMatch(c.path)) return true;
            if (c.path === '/genres' && genreSeriesMatch) return true;
            if (c.path === '/series' && seriesIdMatch) return true;
            return false;
        });
        return false;
    };

    return (
        <>
            {loading ? <Loader /> :
                <>
                    <header className="topnav-header">
                        <div className="topnav-header-inner">
                            <div className="topnav-left">
                                <Link to="/" className="topnav-logo">
                                    <img src={theme === 'light' ? '/assets/images/logo-light.png' : (siteData?.logo || '/assets/images/logo-dark.png')} alt="HikeCinema" loading="lazy" />
                                </Link>
                                <button className="topnav-hamburger" onClick={() => setMobileMenuOpen(prev => !prev)} aria-label="Toggle menu">
                                    <i className={mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'}></i>
                                </button>
                            </div>

                            <nav className={`topnav-menu ${mobileMenuOpen ? 'open' : ''}`} ref={navRef}>
                                {visibleNavMenus.map((menu, idx) => (
                                    menu.children ? (
                                        <div
                                            key={idx}
                                            className={`topnav-dropdown ${openDropdown === idx ? 'open' : ''} ${isMenuActive(menu) ? 'active' : ''}`}
                                        >
                                            <button
                                                className="topnav-dropdown-toggle"
                                                onClick={(e) => { e.preventDefault(); setOpenDropdown(prev => prev === idx ? null : idx); }}
                                            >
                                                <i className={menu.icon}></i>
                                                <span>{menu.label}</span>
                                                <i className="fas fa-chevron-down topnav-chevron"></i>
                                            </button>
                                            <div className="topnav-dropdown-menu">
                                                {menu.children.map((child, ci) => (
                                                    <Link
                                                        key={ci}
                                                        to={child.path}
                                                        className={`topnav-dropdown-item ${isRouteMatch(child.path) ? 'active' : ''}`}
                                                    >
                                                        <i className={child.icon}></i>
                                                        <span>{child.label}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <Link
                                            key={idx}
                                            to={menu.path}
                                            className={`topnav-link ${isMenuActive(menu) ? 'active' : ''}`}
                                        >
                                            <i className={menu.icon}></i>
                                            <span>{menu.label}</span>
                                        </Link>
                                    )
                                ))}
                            </nav>

                            <div className="topnav-right">
                                <button className="theme-toggle-btn" onClick={toggleTheme} aria-label="Toggle theme">
                                    <i className={theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'}></i>
                                </button>
                                <div className="user-profile">
                                    <div className="dropdown">
                                        <div className="d-flex align-items-center gap-2 dropdown-toggle" type="button"
                                            id="profileDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                            <img src={user?.profile_image ?? '/assets/images/profil-img-1.png'} alt="Profile" className="rounded-circle profile_image" width="32" height="32" />
                                            <span id='profile_name'>{user?.name}</span>
                                        </div>
                                        <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="profileDropdown">
                                            <li>
                                                <Link to="/profile" id="profile-btn" className="dropdown-item" tabIndex="0">
                                                    Profile
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/change-password" className="dropdown-item" tabIndex="0">
                                                    Change Password
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="#" className="dropdown-item" onClick={logout} tabIndex="0">
                                                    Log Out
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    {mobileMenuOpen && <div className="topnav-overlay" onClick={() => setMobileMenuOpen(false)}></div>}

                    <main className="main-content topnav-layout">
                        <Breadcrumbs />

                        {/* Nothing viewable at all is a role-configuration mistake, not a
                            routing one. Rendered inside the shell so the profile menu — and
                            with it Log out — stays reachable. */}
                        {currentRoute === ''                  && (needsLandingRedirect ? <NoSectionsAssigned /> : guard(PERM.DASHBOARD, <Dashboard />))}
                        {currentRoute === '/analytics'         && guard(PERM.ANALYTICS,          <AnalyticsDashboard />)}
                        {currentRoute === '/users'             && guard(PERM.USERS,              <Users />)}
                        {userIdMatch                           && guard(PERM.USERS,              <ViewUser id={userIdMatch[1]} />)}
                        {currentRoute === '/series'            && guard(PERM.SERIES,             <Content />)}
                        {seriesIdMatch                         && guard(PERM.SERIES,             <Episodes id={seriesIdMatch[1]} />)}
                        {currentRoute === '/genres'            && guard(PERM.CATEGORIES,         <Genres />)}
                        {genreSeriesMatch                      && guard(PERM.CATEGORIES,         <GenreSeries id={genreSeriesMatch[1]} categoryName={location.state?.categoryName || ''} />)}
                        {currentRoute === '/tags'              && guard(PERM.GENRES,             <Tags />)}
                        {currentRoute === '/types'             && guard(PERM.TAGS,               <Types />)}
                        {currentRoute === '/banner-management' && guard(PERM.BANNER_MANAGEMENT,  <BannerManagement />)}
                        {currentRoute === '/curated'           && guard([PERM.SERIES, PERM.TRAILER_RANKING], <CurationTabs />)}
                        {currentRoute === '/bulk-series-import' && guard('bulk_series_import',    <BulkSeriesImport />)}
                        {currentRoute === '/plans'             && guard(PERM.PACKAGES,           <Plans />)}
                        {currentRoute === '/vip-plans'         && guard(PERM.PACKAGES,           <VipPlans />)}
                        {currentRoute === '/order-history'     && guard('order_history',         <OrderHistory />)}
                        {currentRoute === '/payment-method'    && guard(PERM.PAYMENT_METHOD,     <PaymentMethod />)}
                        {currentRoute === '/languages'         && guard(PERM.LANGUAGES,          <Languages />)}
                        {currentRoute === '/reward'            && guard(PERM.REWARD,             <Reward />)}
                        {currentRoute === '/settings'          && guard(PERM.SETTINGS,           <Settings />)}
                        {currentRoute === '/ads-setting'       && guard(PERM.ADS_SETTING,        <AdsSetting />)}
                        {adsIdMatch                            && guard(PERM.ADS_SETTING,        <AdsSettingUpdate id={adsIdMatch[1]} />)}
                        {currentRoute === '/send-notification' && guard(PERM.SEND_NOTIFICATION,  <SendNotification />)}
                        {currentRoute === '/tickets'           && guard(PERM.TICKETS,            <Tickets />)}
                        {currentRoute === '/activity-feed'     && guard('activity_feed',         <ActivityFeed />)}
                        {currentRoute === '/role-management'   && guard(PERM.ROLE_MANAGEMENT,    <RoleManagement />)}
                        {currentRoute === '/admin-users'       && guard(PERM.ADMIN_USERS,        <AdminUsers />)}
                        {currentRoute === '/user-segments'     && guard(PERM.USER_SEGMENTS,      <UserSegments />)}
                        {currentRoute === '/profile'           && <Profile />}
                        {currentRoute === '/about-us'          && guard(PERM.ABOUT_US,           <AboutUs />)}
                        {currentRoute === '/privacy-policy'    && guard(PERM.PRIVACY_POLICY,     <PrivacyPolicy />)}
                        {currentRoute === '/terms-conditions'  && guard(PERM.TERMS_AND_CONDITIONS, <TermsCondition />)}
                        {currentRoute === '/media-gallery'     && <MediaGallery />}
                        {currentRoute === '/top-contents'      && <TopContents />}
                        {currentRoute === '/actors'            && <Actors />}
                        {currentRoute === '/live-tv-categories'&& <LiveTvCategories />}
                        {currentRoute === '/live-tv-channels'  && <LiveTvChannels />}
                        {currentRoute === '/notification'      && <Notification />}
                        {currentRoute === '/movies'            && <Movies />}
                        {currentRoute === '/tv-shows'          && <TvShows />}
                        {currentRoute === '/reports'           && <Reports />}
                        {currentRoute === '/content-moderation' && guard('content_moderation', <ContentModeration />)}
                        {currentRoute === '/real-time-analytics'&& <RealTimeAnalytics />}
                        {currentRoute === '/system-health'     && <SystemHealth />}
                        {currentRoute === '/video-pipeline'    && <VideoPipeline />}
                        {currentRoute === '/player-analytics'  && <PlayerAnalytics />}
                        {currentRoute === '/revenue'           && <RevenueMonetization />}
                        {currentRoute === '/security'          && <SecurityCompliance />}
                        {currentRoute === '/infrastructure'    && <InfrastructureCost />}
                        {currentRoute === '/codebase'          && <CodebaseDevOps />}

                        <ScrollProgress />
                    </main>
                </>
            }
        </>
    );
};

export default Main;
